import React, { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Input } from './ui/input'
import { getFormattedAddresses } from '@/utils/helper'

interface GooglePlaceAutoCompleteProps {
  placeholder?: string
  className?: string
  value?: string
  handleChange?: (e: ChangeEvent<HTMLInputElement>) => void
  handlePlaceSelect?: (placeData: PlaceData) => void
}

export interface PlaceData {
  address: string
  country: string
  state: string
  city: string
  lat: number
  lng: number
  zipCode: string
}

const GooglePlaceAutoComplete: React.FC<GooglePlaceAutoCompleteProps> = ({
  placeholder = 'Enter address',
  className = '',
  value,
  handleChange,
  handlePlaceSelect,
}) => {
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)

  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAP_API_KEY}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = () => {
        serviceRef.current = new google.maps.places.AutocompleteService()
        mapRef.current = new google.maps.Map(document.createElement('div'))
      }
      document.body.appendChild(script)
    } else {
      serviceRef.current = new google.maps.places.AutocompleteService()
      mapRef.current = new google.maps.Map(document.createElement('div'))
    }
  }, [])

  const fetchPredictions = (input: string): void => {
    if (input && serviceRef.current) {
      serviceRef.current.getPlacePredictions(
        {
          input,
        },
        (
          predictions: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions || [])
            setOpen((predictions?.length ?? 0) > 0)
          } else {
            setSuggestions([])
            setOpen(false)
          }
        }
      )
    } else {
      setSuggestions([])
      setOpen(false)
    }
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (handleChange) {
      handleChange(e)
    }
    fetchPredictions(e.target.value)
    setActiveIndex(-1)
  }

  const onSelect = (placeId: string): void => {
    setSuggestions([])
    setOpen(false)
    setActiveIndex(-1)

    if (!mapRef.current) return

    const placesService = new google.maps.places.PlacesService(mapRef.current)

    placesService.getDetails(
      {
        placeId,
        fields: [
          'name',
          'formatted_address',
          'geometry',
          'address_components',
          'types',
        ],
      },
      (
        place: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const components = place.address_components || []

          const { manual_formatted_Address, country, state, city, zipCode } =
            getFormattedAddresses(place, components)

          const lat = place.geometry?.location?.lat()
          const lng = place.geometry?.location?.lng()

          if (lat !== undefined && lng !== undefined && handlePlaceSelect) {
            handlePlaceSelect({
              address:
                manual_formatted_Address || place.formatted_address || '',
              country,
              state,
              city,
              lat,
              lng,
              zipCode,
            })
          }
        }
      }
    )
  }

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1))
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault()
        onSelect(suggestions[activeIndex].place_id)
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  return (
    <div className="relative w-full">
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        className={`w-full ${className}`}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />

      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map(
            (sug: google.maps.places.AutocompletePrediction, idx) => (
              <button
                key={sug.place_id}
                onClick={() => onSelect(sug.place_id)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`px-3 py-2 cursor-pointer text-sm hover:bg-gray-100 d-block w-full justify-start text-left ${
                  idx === activeIndex ? 'bg-gray-100' : ''
                }`}
              >
                {sug.description}
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}

export default GooglePlaceAutoComplete
